/* eslint-disable no-unused-vars */

import React, { useState, useEffect, useContext, useMemo } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";

import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import CampaignModalPhrase from "../../components/CampaignModalPhrase";
import {
  AddCircle,
  TextFields,
  Search as SearchIcon,
  ViewList,
  ViewModule,
} from "@mui/icons-material";
import {
  CircularProgress,
  Grid,
  Stack,
  Chip,
  Box,
  Tooltip,
  TextField,
  InputAdornment,
  Card,
  CardContent,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Can } from "../../components/Can";
import {
  colorBackgroundTable,
  colorLineTable,
  colorLineTableHover,
  colorTopTable,
} from "../../styles/styles";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    backgroundColor: colorBackgroundTable(),
    borderRadius: 12,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  whatsappChip: {
    margin: "2px",
    fontSize: "0.75rem",
    height: "20px",
  },
  connectionContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: "4px",
    alignItems: "center",
  },
  viewToggle: {
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  viewToggleBtn: {
    minWidth: 36,
    height: 36,
    padding: "4px 8px",
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: "transparent",
    color: theme.palette.text.secondary,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.15s",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  viewToggleBtnActive: {
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    borderColor: theme.palette.primary.main,
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  cardsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
    gap: theme.spacing(2),
    padding: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
      gap: theme.spacing(1.5),
    },
  },
  phraseCard: {
    // MUI v5 Card ignora o ThemeProvider v4 → fundo branco + texto claro some no dark
    backgroundColor:
      theme.palette.type === "dark"
        ? `${theme.palette.optionsBackground || "#333"} !important`
        : `${theme.palette.background.paper || "#fff"} !important`,
    color: theme.palette.type === "dark" ? "#f5f5f5" : theme.palette.text.primary,
    borderRadius: 16,
    border: `1px solid ${theme.palette.divider}`,
    height: "100%",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    cursor: "pointer",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: theme.shadows[4],
      borderColor: theme.palette.primary.light,
    },
  },
  phraseCardIcon: {
    width: 44,
    height: 44,
    borderRadius: 12,
    backgroundColor: theme.palette.primary.main,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: theme.palette.primary.contrastText,
    marginBottom: theme.spacing(1.5),
  },
  phraseCardName: {
    fontWeight: 600,
    fontSize: "1rem",
    color:
      theme.palette.type === "dark"
        ? "#f5f5f5 !important"
        : `${theme.palette.text.primary} !important`,
    marginBottom: theme.spacing(0.5),
    wordBreak: "break-word",
  },
  phraseCardMeta: {
    color:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.7) !important"
        : `${theme.palette.text.secondary} !important`,
    fontSize: "0.8rem",
    marginBottom: theme.spacing(1.5),
  },
  phraseCardFooter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: "auto",
    gap: theme.spacing(1),
  },
  phraseCardAction: {
    color:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.75)"
        : theme.palette.text.secondary,
  },
}));

const countPhrases = (phrase) => {
  if (!phrase) return 0;
  if (Array.isArray(phrase)) return phrase.length;
  if (typeof phrase === "string") {
    try {
      const parsed = JSON.parse(phrase);
      return Array.isArray(parsed) ? parsed.length : 1;
    } catch {
      return phrase.trim() ? 1 : 0;
    }
  }
  if (typeof phrase === "object") return 1;
  return 0;
};

const CampaignsPhrase = () => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(true);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [deletingContact, setDeletingContact] = useState(null);

  const [campaignflows, setCampaignFlows] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [ModalOpenPhrase, setModalOpenPhrase] = useState(false);
  const [campaignflowSelected, setCampaignFlowSelected] = useState();
  const [whatsApps, setWhatsApps] = useState([]);

  const [viewMode, setViewMode] = useState(() => {
    try {
      const saved = localStorage.getItem("phraseLists_viewMode");
      return saved === "cards" || saved === "list" ? saved : "list";
    } catch {
      return "list";
    }
  });

  const handleChangeViewMode = (mode) => {
    setViewMode(mode);
    try {
      localStorage.setItem("phraseLists_viewMode", mode);
    } catch {
      /* ignore */
    }
  };

  const handleDeleteCampaign = async (campaignId) => {
    try {
      await api.delete(`/flowcampaign/${campaignId}`);
      toast.success("Campanha deletada com sucesso");
      getCampaigns();
    } catch (err) {
      toastError(err);
    }
    setConfirmModalOpen(false);
    setDeletingContact(null);
  };

  const getWhatsApps = async () => {
    try {
      const response = await api.get("/whatsapp");
      setWhatsApps(response.data || []);
    } catch (error) {
      console.error("Erro ao buscar WhatsApps:", error);
    }
  };

  const getCampaigns = async () => {
    try {
      setLoading(true);
      const response = await api.get("/flowcampaign");

      let flowData = [];

      if (response.data) {
        if (Array.isArray(response.data.flow)) {
          flowData = response.data.flow;
        } else if (response.data.data && Array.isArray(response.data.data.flow)) {
          flowData = response.data.data.flow;
        } else if (Array.isArray(response.data.data)) {
          flowData = response.data.data;
        } else if (Array.isArray(response.data)) {
          flowData = response.data;
        } else {
          const dataKeys = Object.keys(response.data);
          for (const key of dataKeys) {
            if (Array.isArray(response.data[key])) {
              flowData = response.data[key];
              break;
            }
          }
        }
      }

      setCampaignFlows(flowData);
    } catch (error) {
      console.error("Erro ao buscar campanhas:", error);
      toastError(error);
      setCampaignFlows([]);
    } finally {
      setLoading(false);
    }
  };

  const onSaveModal = () => {
    getCampaigns();
  };

  const openEdit = (flowId) => {
    setCampaignFlowSelected(flowId);
    setModalOpenPhrase(true);
  };

  const renderWhatsAppConnections = (whatsappIds) => {
    if (!whatsappIds || !Array.isArray(whatsappIds) || whatsappIds.length === 0) {
      return (
        <Typography variant="body2" color="textSecondary">
          Nenhuma conexão
        </Typography>
      );
    }

    if (whatsappIds.length > 3) {
      const firstTwo = whatsappIds.slice(0, 2);
      const remaining = whatsappIds.length - 2;

      return (
        <Box className={classes.connectionContainer}>
          {firstTwo.map((whatsappId) => {
            const whatsapp = whatsApps.find((w) => w.id === whatsappId);
            return (
              <Tooltip
                key={whatsappId}
                title={
                  whatsapp
                    ? `${whatsapp.name} (${whatsapp.status})`
                    : `ID: ${whatsappId}`
                }
              >
                <Chip
                  label={whatsapp ? whatsapp.name : `ID: ${whatsappId}`}
                  className={classes.whatsappChip}
                  size="small"
                  color={whatsapp?.status === "CONNECTED" ? "success" : "default"}
                  variant="outlined"
                />
              </Tooltip>
            );
          })}
          <Tooltip title={`Mais ${remaining} conexão(ões)`}>
            <Chip
              label={`+${remaining}`}
              className={classes.whatsappChip}
              size="small"
              color="primary"
              variant="outlined"
            />
          </Tooltip>
        </Box>
      );
    }

    return (
      <Box className={classes.connectionContainer}>
        {whatsappIds.map((whatsappId) => {
          const whatsapp = whatsApps.find((w) => w.id === whatsappId);
          return (
            <Tooltip
              key={whatsappId}
              title={
                whatsapp
                  ? `${whatsapp.name} (${whatsapp.status})`
                  : `ID: ${whatsappId}`
              }
            >
              <Chip
                label={whatsapp ? whatsapp.name : `ID: ${whatsappId}`}
                className={classes.whatsappChip}
                size="small"
                color={whatsapp?.status === "CONNECTED" ? "success" : "default"}
                variant="outlined"
              />
            </Tooltip>
          );
        })}
      </Box>
    );
  };

  const filteredCampaignflows = useMemo(() => {
    if (!Array.isArray(campaignflows) || campaignflows.length === 0) {
      return [];
    }
    const q = searchQuery.trim().toLowerCase();
    if (!q) {
      return campaignflows;
    }
    return campaignflows.filter((flow) => {
      const name = (flow.name || "").toLowerCase();
      if (name.includes(q)) return true;
      if (String(flow.id).includes(q)) return true;
      return false;
    });
  }, [campaignflows, searchQuery]);

  useEffect(() => {
    getCampaigns();
    getWhatsApps();
  }, []);

  const renderListRow = (flow) => (
    <Grid
      container
      key={flow.id}
      sx={{
        padding: "8px",
        backgroundColor: colorLineTable(),
        borderRadius: 4,
        marginTop: 0.5,
        "&:hover": {
          backgroundColor: colorLineTableHover(),
        },
      }}
    >
      <Grid item xs={3}>
        <Stack justifyContent={"center"} height={"100%"} style={{ color: "#ededed" }}>
          <Stack direction={"row"} alignItems="center">
            <TextFields />
            <Stack justifyContent={"center"} marginLeft={1}>
              <Typography variant="body2" style={{ fontWeight: 500 }}>
                {flow.name || "Nome não definido"}
              </Typography>
              {countPhrases(flow.phrase) > 0 && (
                <Typography variant="caption" style={{ color: "#bbb" }}>
                  {countPhrases(flow.phrase)} frase(s) configurada(s)
                </Typography>
              )}
            </Stack>
          </Stack>
        </Stack>
      </Grid>

      <Grid item xs={3} align="center">
        <Stack justifyContent={"center"} height={"100%"}>
          {renderWhatsAppConnections(flow.whatsappIds)}
        </Stack>
      </Grid>

      <Grid item xs={3} align="center" style={{ color: "#ededed" }}>
        <Stack justifyContent={"center"} height={"100%"}>
          <Chip
            label={flow.status ? "Ativo" : "Desativado"}
            size="small"
            color={flow.status ? "success" : "default"}
            variant={flow.status ? "filled" : "outlined"}
          />
        </Stack>
      </Grid>

      <Grid item xs={3} align="end">
        <Stack direction="row" justifyContent="flex-end" alignItems="center">
          <IconButton
            size="small"
            onClick={() => openEdit(flow.id)}
            title="Editar campanha"
          >
            <EditIcon style={{ color: "#ededed" }} />
          </IconButton>
          <Can
            role={user.profile}
            perform="contacts-page:deleteContact"
            yes={() => (
              <IconButton
                size="small"
                onClick={() => {
                  setConfirmModalOpen(true);
                  setDeletingContact(flow);
                }}
                title="Excluir campanha"
              >
                <DeleteOutlineIcon style={{ color: "#ededed" }} />
              </IconButton>
            )}
          />
        </Stack>
      </Grid>
    </Grid>
  );

  const renderCard = (flow) => {
    const phrasesCount = countPhrases(flow.phrase);
    return (
      <Card
        key={flow.id}
        className={classes.phraseCard}
        elevation={0}
        onClick={() => openEdit(flow.id)}
      >
        <CardContent
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            p: 2.5,
            "&:last-child": { pb: 2.5 },
          }}
        >
          <div className={classes.phraseCardIcon}>
            <TextFields style={{ fontSize: 22 }} />
          </div>
          <Typography className={classes.phraseCardName}>
            {flow.name || "Nome não definido"}
          </Typography>
          <Typography className={classes.phraseCardMeta}>
            {phrasesCount > 0
              ? `${phrasesCount} frase(s) configurada(s)`
              : "Sem frases configuradas"}
          </Typography>

          <Box mb={1.5}>{renderWhatsAppConnections(flow.whatsappIds)}</Box>

          <div className={classes.phraseCardFooter}>
            <Chip
              label={flow.status ? "Ativo" : "Desativado"}
              size="small"
              color={flow.status ? "success" : "default"}
              variant={flow.status ? "filled" : "outlined"}
            />
            <Stack direction="row" spacing={0.5}>
              <IconButton
                size="small"
                className={classes.phraseCardAction}
                onClick={(e) => {
                  e.stopPropagation();
                  openEdit(flow.id);
                }}
                title="Editar campanha"
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <Can
                role={user.profile}
                perform="contacts-page:deleteContact"
                yes={() => (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setConfirmModalOpen(true);
                      setDeletingContact(flow);
                    }}
                    title="Excluir campanha"
                  >
                    <DeleteOutlineIcon
                      fontSize="small"
                      style={{ color: theme.palette.error.main }}
                    />
                  </IconButton>
                )}
              />
            </Stack>
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderEmpty = (message, subtitle) => (
    <Stack
      justifyContent={"center"}
      alignItems={"center"}
      minHeight={"20vh"}
      style={{ color: "#ededed" }}
    >
      <Typography variant="body1">{message}</Typography>
      {subtitle && (
        <Typography variant="body2" style={{ marginTop: "8px", color: "#bbb" }}>
          {subtitle}
        </Typography>
      )}
    </Stack>
  );

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          deletingContact &&
          `${i18n.t("campaigns.confirmationModal.deleteTitle")} ${
            deletingContact.name
          }?`
        }
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => handleDeleteCampaign(deletingContact.id)}
      >
        {i18n.t("campaigns.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <CampaignModalPhrase
        open={ModalOpenPhrase}
        onClose={() => setModalOpenPhrase(false)}
        FlowCampaignId={campaignflowSelected}
        onSave={onSaveModal}
      />
      <MainHeader>
        <Grid style={{ width: "99.6%" }} container alignItems="center">
          <Grid xs={12} sm={5} item>
            <Title>Campanhas de Fluxo</Title>
          </Grid>
          <Grid xs={12} sm={7} item>
            <Grid spacing={1} container justifyContent="flex-end" alignItems="center">
              <Grid item>
                <div className={classes.viewToggle}>
                  <Tooltip title="Lista">
                    <button
                      type="button"
                      className={`${classes.viewToggleBtn} ${
                        viewMode === "list" ? classes.viewToggleBtnActive : ""
                      }`}
                      onClick={() => handleChangeViewMode("list")}
                      aria-label="Visualização em lista"
                    >
                      <ViewList style={{ fontSize: 18 }} />
                    </button>
                  </Tooltip>
                  <Tooltip title="Cards">
                    <button
                      type="button"
                      className={`${classes.viewToggleBtn} ${
                        viewMode === "cards" ? classes.viewToggleBtnActive : ""
                      }`}
                      onClick={() => handleChangeViewMode("cards")}
                      aria-label="Visualização em cards"
                    >
                      <ViewModule style={{ fontSize: 18 }} />
                    </button>
                  </Tooltip>
                </div>
              </Grid>
              <Grid xs={12} sm={5} item>
                <TextField
                  size="small"
                  fullWidth
                  placeholder={i18n.t("campaignsPhrase.searchCampaign")}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  variant="outlined"
                />
              </Grid>
              <Grid xs={12} sm={4} item>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => {
                    setCampaignFlowSelected(undefined);
                    setModalOpenPhrase(true);
                  }}
                  color="primary"
                  style={{ textTransform: "none" }}
                >
                  <Stack direction={"row"} gap={1}>
                    <AddCircle />
                    {"Nova Campanha"}
                  </Stack>
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        {loading ? (
          <Stack justifyContent={"center"} alignItems={"center"} minHeight={"50vh"}>
            <CircularProgress />
          </Stack>
        ) : !campaignflows ||
          !Array.isArray(campaignflows) ||
          campaignflows.length === 0 ? (
          renderEmpty(
            "Nenhuma campanha encontrada",
            'Clique em "Nova Campanha" para criar sua primeira campanha de fluxo'
          )
        ) : filteredCampaignflows.length === 0 ? (
          renderEmpty(i18n.t("campaignsPhrase.noSearchResults"))
        ) : viewMode === "cards" ? (
          <div className={classes.cardsGrid}>
            {filteredCampaignflows.map((flow) => renderCard(flow))}
          </div>
        ) : (
          <Stack>
            <Grid container style={{ padding: "8px" }}>
              <Grid item xs={3} style={{ color: colorTopTable() }}>
                Nome
              </Grid>
              <Grid item xs={3} style={{ color: colorTopTable() }} align="center">
                Conexões
              </Grid>
              <Grid item xs={3} style={{ color: colorTopTable() }} align="center">
                Status
              </Grid>
              <Grid item xs={3} align="end" style={{ color: colorTopTable() }}>
                {i18n.t("contacts.table.actions")}
              </Grid>
            </Grid>
            {filteredCampaignflows.map((flow) => renderListRow(flow))}
          </Stack>
        )}
      </Paper>
    </MainContainer>
  );
};

export default CampaignsPhrase;
